import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToken } from '@/common/helper';
import useLiteDropdown from '@/common/helper/useLiteDropdown';
import { useUnAuthorize } from '@/hooks';
import type {
  PaginatedRequest,
  PaginatedResponse,
} from '@/common/helper/usePaginatedDropdown';
import { GetActivityUsersLite, type ActivityUserLite } from '@/apis/activity';
import { usersChipLabel } from '../../utils/filterEvents/filterEvents';
import {
  ActivityFeedInitialUser,
  isInclusionPayload,
  UserSelectionPayload,
} from '../../types';

export function useActivityUsersFilter(
  initialUser?: ActivityFeedInitialUser | null,
  restoreSelection?: UserSelectionPayload | null
) {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();

  // Own fetchFn — deliberately not the Settings > Users table's shared
  // useFetchUsers, since that one's endpoint only returns active-or-disabled
  // depending on a toggle, never both, and this chip always needs everyone.
  const fetchFn = useCallback(
    async ({
      page,
      search,
    }: PaginatedRequest): Promise<PaginatedResponse<ActivityUserLite>> => {
      try {
        const response = await GetActivityUsersLite(
          token,
          page,
          search || undefined
        );
        const resData = response?.data?.data;
        const rawItems = Array.isArray(resData?.data) ? resData.data : [];
        const pageCount = resData?.page_count ?? 1;
        return {
          data: rawItems,
          total: resData?.total_count ?? 0,
          pageCount,
          hasNext: page < pageCount,
        };
      } catch (error) {
        unauthorize?.(error);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [token, unauthorize]
  );

  const {
    data: fetchedUsers,
    hasMore,
    search,
    setSearch,
    handlePopupScroll,
    total,
  } = useLiteDropdown<ActivityUserLite>({ fetchFn, initialLimit: 50 });

  // `total` tracks whatever the current search matches — freeze it here at
  // the company-wide figure (search === '') so narrowing the dropdown's own
  // search doesn't shrink the "All users" pill outside it.
  const [companyTotalUsers, setCompanyTotalUsers] = useState(0);
  useEffect(() => {
    if (search === '') setCompanyTotalUsers(total);
  }, [search, total]);

  // Row-open's initialUser needs to count for selection math even before
  // this panel's own fetch has reached them.
  const effectiveUsers = useMemo(() => {
    if (!initialUser) return fetchedUsers;
    const id = String(initialUser.id);
    if (fetchedUsers.some((user) => user.id === id)) return fetchedUsers;
    return [
      { id, name: initialUser.name, type: 'Active' as const },
      ...fetchedUsers,
    ];
  }, [fetchedUsers, initialUser]);

  const [selectAll, setSelectAll] = useState(() =>
    restoreSelection ? restoreSelection.selectAll : !initialUser
  );
  const [excludedUserIds, setExcludedUserIds] = useState<Set<string>>(
    () =>
      new Set(
        restoreSelection?.selectAll ? restoreSelection.excludedUserIds : []
      )
  );
  const [includedUserIds, setIncludedUserIds] = useState<Set<string>>(() => {
    if (restoreSelection) {
      return isInclusionPayload(restoreSelection)
        ? new Set(restoreSelection.userIds)
        : new Set<string>();
    }
    return initialUser ? new Set([String(initialUser.id)]) : new Set();
  });

  const resetSelection = (
    nextInitialUser?: ActivityFeedInitialUser | null,
    nextRestoreSelection?: UserSelectionPayload | null
  ) => {
    setSelectAll(
      nextRestoreSelection ? nextRestoreSelection.selectAll : !nextInitialUser
    );
    setExcludedUserIds(
      new Set(
        nextRestoreSelection?.selectAll
          ? nextRestoreSelection.excludedUserIds
          : []
      )
    );
    setIncludedUserIds(
      nextRestoreSelection && isInclusionPayload(nextRestoreSelection)
        ? new Set(nextRestoreSelection.userIds)
        : nextInitialUser
          ? new Set([String(nextInitialUser.id)])
          : new Set()
    );
  };

  const resetSearchAndPaging = () => {
    setSearch('');
  };

  const isUserSelected = (id: string) =>
    selectAll ? !excludedUserIds.has(id) : includedUserIds.has(id);

  const onToggleUser = (id: string, checked: boolean) => {
    if (selectAll) {
      setExcludedUserIds((previous) => {
        const next = new Set(previous);
        if (checked) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setIncludedUserIds((previous) => {
        const next = new Set(previous);
        if (checked) next.add(id);
        else next.delete(id);
        return next;
      });
    }
  };

  const onToggleAll = (checked: boolean) => {
    setSelectAll(checked);
    setExcludedUserIds(new Set());
    setIncludedUserIds(new Set());
  };

  const selectOnlyUser = (id: string) => {
    setSelectAll(false);
    setExcludedUserIds(new Set());
    setIncludedUserIds(new Set([id]));
  };

  const allSelected = selectAll && excludedUserIds.size === 0;

  // Pending users' checkboxes are disabled — they can never be individually
  // excluded, so they never count as "selected" (they have no activity anyway).
  const loadedPendingCount = useMemo(
    () => effectiveUsers.filter((user) => user.type === 'No activity').length,
    [effectiveUsers]
  );
  const selectableTotalUsers = Math.max(
    companyTotalUsers - loadedPendingCount,
    0
  );
  const selectedCount = selectAll
    ? Math.max(selectableTotalUsers - excludedUserIds.size, 0)
    : includedUserIds.size;
  const usersLabel = usersChipLabel(selectedCount, selectableTotalUsers);
  const usersActive = !allSelected;

  // Only used here for singleUserMode/subjectUser below — "select all"
  // only ever reasons about known users, never ones nobody's fetched yet.
  const resolvedSelectedUserIds = useMemo(() => {
    if (!selectAll) return includedUserIds;
    return new Set(
      effectiveUsers
        .filter(
          (user) => user.type !== 'No activity' && !excludedUserIds.has(user.id)
        )
        .map((user) => user.id)
    );
  }, [selectAll, includedUserIds, excludedUserIds, effectiveUsers]);

  const singleUserMode = resolvedSelectedUserIds.size === 1;
  const subjectUser = singleUserMode
    ? effectiveUsers.find((user) => user.id === [...resolvedSelectedUserIds][0])
    : undefined;

  const selectionPayload = useMemo<UserSelectionPayload>(
    () =>
      selectAll
        ? { selectAll: true, excludedUserIds: [...excludedUserIds] }
        : { selectAll: false, userIds: [...includedUserIds] },
    [selectAll, excludedUserIds, includedUserIds]
  );

  return {
    fetchedUsers,
    singleUserMode,
    subjectUser,
    usersLabel,
    usersActive,
    isUserSelected,
    onToggleUser,
    allSelected,
    onToggleAll,
    selectOnlyUser,
    search,
    onSearchChange: setSearch,
    hasMore,
    handlePopupScroll,
    selectionPayload,
    resetSelection,
    resetSearchAndPaging,
  };
}

export default useActivityUsersFilter;
